/**
 * PostOverlayContext - Lets any deeply-nested component (PostFeedItem, search
 * results, notifications, etc.) open the mobile post-detail overlay without
 * having to know where the state lives.
 *
 * AppLayout is the provider — it owns the overlay state and renders
 * `<PostOverlay />` itself. Consumers call `openPost(postId)` to request the
 * overlay; the hook also reports whether we're on a mobile viewport so call
 * sites can fall back to in-place navigation on desktop.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

const PostOverlayContext = createContext(null);

const MOBILE_QUERY = '(max-width: 767px)';

function readIsMobile() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  // `matchMedia` may return undefined under some jest mocks; fall back gracefully.
  const mql = window.matchMedia(MOBILE_QUERY);
  return Boolean(mql && mql.matches);
}

export function PostOverlayProvider({ children, openPost, closePost, postId }) {
  const [isMobile, setIsMobile] = useState(readIsMobile);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    if (!mql) return undefined;
    const onChange = (e) => setIsMobile(Boolean(e?.matches));
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (mql.addListener) {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, []);

  const value = { isMobile, postId, openPost, closePost };
  return <PostOverlayContext.Provider value={value}>{children}</PostOverlayContext.Provider>;
}

export function usePostOverlay() {
  // Default no-op so components used outside AppLayout (direct /posts/:id
  // route rendered standalone, unit tests, etc.) keep working — they'll just
  // navigate.
  return (
    useContext(PostOverlayContext) || {
      isMobile: false,
      postId: null,
      openPost: () => {},
      closePost: () => {},
    }
  );
}

export default PostOverlayContext;
